import React, { useState } from 'react'
import { Box, Button, CircularProgress, IconButton, MenuItem, TextField, Typography } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { Redirect } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getRoleEnum, Role, roleLabel } from '@types'
import { bankList } from '@views/signupView'
import { userProfile } from '@views/profile/state/profileSlice'
import { formatTimestamp } from '@officeTimeTracker/utils/timeTrackerUi'
import useAdminHoursMetrics from './state/useAdminHoursMetrics'
import useAdminSavedInstallmentsMetrics from './state/useAdminSavedInstallmentsMetrics'
import { AdminMetricsModule, AdminSavedInstallmentsFilters, SavedInstallmentsFilterMode } from './types'
import './adminMetrics.css'

const formatDateLabel = (dateValue: string) =>
  new Intl.DateTimeFormat('es-CO', {
    timeZone: 'America/Bogota',
    weekday: 'long',
    day: '2-digit',
    month: 'short'
  }).format(new Date(`${dateValue}T12:00:00-05:00`))

const formatRoleLabel = (role?: string) => {
  if (!role) {
    return '--'
  }

  return roleLabel[getRoleEnum(role)]
}

const getPauseLabel = (pauseType: 'break' | 'almuerzo') => (pauseType === 'break' ? 'Pausa activa' : 'Pausa almuerzo')

const formatLocationTypeLabel = (locationType?: 'office' | 'campaign' | null) => {
  if (locationType === 'office') {
    return 'oficina'
  }

  if (locationType === 'campaign') {
    return 'campaña'
  }

  return '--'
}

const formatOfficeName = (officeName?: string | null) => {
  if (!officeName) {
    return ''
  }

  return officeName
    .split(' ')
    .filter(Boolean)
    .map(part => {
      if (part.toUpperCase() === part) {
        return part
      }

      return `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`
    })
    .join(' ')
}

const formatLocationDetail = (
  locationType?: 'office' | 'campaign',
  address?: string | null,
  officeName?: string | null
) => {
  if (locationType === 'campaign') {
    return address ? `Campaña: ${address}` : 'Campaña: ubicacion no disponible'
  }

  if (locationType === 'office') {
    const formattedOfficeName = formatOfficeName(officeName)
    if (formattedOfficeName) {
      return `Oficina ${formattedOfficeName}`
    }

    return address ? `Oficina: ${address}` : 'Oficina: ubicacion no disponible'
  }

  return 'Ubicacion no registrada'
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 2
  }).format(value)

const formatNumber = (value: number) =>
  new Intl.NumberFormat('es-CO', {
    maximumFractionDigits: 2
  }).format(value)

const INITIAL_SAVED_INSTALLMENTS_FILTERS: AdminSavedInstallmentsFilters = {
  mode: 'global',
  year: '',
  month: '',
  startDate: '',
  endDate: '',
  bank: ''
}

const getSavedInstallmentsBadgeLabel = (filters: AdminSavedInstallmentsFilters) => {
  const periodLabel =
    filters.mode === 'year' && filters.year
      ? `Año ${filters.year}`
      : filters.mode === 'month' && filters.month
      ? `Mes ${filters.month}`
      : filters.mode === 'custom_range' && filters.startDate && filters.endDate
      ? `${filters.startDate} a ${filters.endDate}`
      : 'Resumen global'

  return filters.bank ? `${periodLabel} · ${filters.bank}` : periodLabel
}

const AdminMetricsView: React.FC = () => {
  const { role } = useSelector(userProfile)
  const [activeModule, setActiveModule] = useState<AdminMetricsModule>('hours')
  const [savedInstallmentsFilters, setSavedInstallmentsFilters] = useState<AdminSavedInstallmentsFilters>(
    INITIAL_SAVED_INSTALLMENTS_FILTERS
  )
  const isHoursModule = activeModule === 'hours'
  const {
    averageSecondsPerEmployee,
    bootstrapping,
    bootstrapReady,
    error,
    formatSeconds,
    hasActiveSearch,
    loadingEntries,
    loadingSelectedEmployeePauses,
    mode,
    referenceDate,
    referenceMonth,
    resetSearch,
    searchEmployees,
    searchTerm,
    searching,
    selectEmployee,
    selectedEmployee,
    selectedEmployeeEntries,
    selectedEmployeePausesByEntryId,
    selectedPeriodLabel,
    setMode,
    setReferenceDate,
    setReferenceMonth,
    setSearchTerm,
    summaries,
    visibleEntriesCount,
    totalSeconds
  } = useAdminHoursMetrics(isHoursModule)
  const {
    error: savedInstallmentsError,
    loading: loadingSavedInstallments,
    summary: savedInstallmentsSummary
  } = useAdminSavedInstallmentsMetrics(savedInstallmentsFilters, activeModule === 'savedInstallments')

  const averagePeriodLabel = mode === 'day' ? 'día' : mode === 'week' ? 'semana' : 'mes'
  const activeError = isHoursModule ? error : savedInstallmentsError

  const updateSavedInstallmentsFilterMode = (nextMode: SavedInstallmentsFilterMode) => {
    setSavedInstallmentsFilters(currentFilters => ({
      ...currentFilters,
      mode: nextMode
    }))
  }

  const updateSavedInstallmentsField = (field: keyof Omit<AdminSavedInstallmentsFilters, 'mode'>, value: string) => {
    setSavedInstallmentsFilters(currentFilters => ({
      ...currentFilters,
      [field]: value
    }))
  }

  if (role !== Role.admin) {
    return <Redirect to="/" />
  }

  return (
    <div className="admin-metrics-page">
      <div className="admin-metrics-shell">
        <aside className="admin-metrics-aside-card">
          <Typography variant="overline" style={{ color: '#b45309', fontWeight: 800 }}>
            Modulos
          </Typography>
          <Box marginTop="12px" marginBottom="12px" display="grid" gridGap="12px">
            <button
              type="button"
              className={`admin-metrics-module-button ${activeModule === 'hours' ? 'active' : ''}`}
              onClick={() => setActiveModule('hours')}
            >
              Registro de horas
            </button>
            <button
              type="button"
              className={`admin-metrics-module-button ${activeModule === 'savedInstallments' ? 'active' : ''}`}
              onClick={() => setActiveModule('savedInstallments')}
            >
              Cuotas ahorradas
            </button>
          </Box>
          <Typography variant="body2" className="admin-metrics-subtle">
            Este panel queda listo para agregar nuevas opciones administrativas sin rediseñar la vista.
          </Typography>
        </aside>

        <section className="admin-metrics-main">
          <div className="admin-metrics-header">
            <div>
              <Typography variant="h4">Metricas</Typography>
              <Typography variant="body1" className="admin-metrics-subtle">
                {isHoursModule
                  ? 'Supervisa las horas registradas por el equipo y filtra por periodo o por empleado.'
                  : 'Segmenta cuotas ahorradas por periodo y banco para comparar ahorro y volumen de casos.'}
              </Typography>
            </div>
            <div className="admin-metrics-period-badge">
              {isHoursModule
                ? `Periodo activo: ${selectedPeriodLabel}`
                : getSavedInstallmentsBadgeLabel(savedInstallmentsFilters)}
            </div>
          </div>

          {isHoursModule ? (
            <div className="admin-metrics-toolbar">
              <div className="admin-metrics-mode-group">
                {(['day', 'week', 'month'] as const).map(currentMode => (
                  <button
                    key={currentMode}
                    type="button"
                    className={`admin-metrics-mode-button ${mode === currentMode ? 'active' : ''}`}
                    onClick={() => setMode(currentMode)}
                  >
                    {currentMode === 'day' ? 'Dia' : currentMode === 'week' ? 'Semana' : 'Mes'}
                  </button>
                ))}

                {mode === 'month' ? (
                  <TextField
                    label="Mes"
                    type="month"
                    value={referenceMonth}
                    onChange={event => setReferenceMonth(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                  />
                ) : (
                  <TextField
                    label={mode === 'day' ? 'Fecha' : 'Fecha de referencia'}
                    type="date"
                    value={referenceDate}
                    onChange={event => setReferenceDate(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                  />
                )}

                {loadingEntries ? (
                  <div className="admin-metrics-inline-loader">
                    <CircularProgress size={16} thickness={5} />
                    <span>Actualizando...</span>
                  </div>
                ) : null}
              </div>

              <form
                className="admin-metrics-search-form"
                onSubmit={event => {
                  event.preventDefault()
                  void searchEmployees()
                }}
              >
                <TextField
                  label="Buscar empleado"
                  value={searchTerm}
                  onChange={event => setSearchTerm(event.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="Nombre del empleado"
                  disabled={searching}
                />
                <Button type="submit" variant="contained" color="primary" disableElevation disabled={searching}>
                  {searching ? (
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <CircularProgress size={16} thickness={5} style={{ color: '#fff' }} />
                      <span>Buscando...</span>
                    </Box>
                  ) : (
                    'Buscar'
                  )}
                </Button>
                {hasActiveSearch ? (
                  <IconButton type="button" className="admin-metrics-clear-button" onClick={resetSearch}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </form>
            </div>
          ) : (
            <div className="admin-metrics-toolbar admin-metrics-toolbar--stacked">
              <div className="admin-metrics-mode-group">
                {([
                  ['global', 'Global'],
                  ['year', 'Año'],
                  ['month', 'Mes'],
                  ['custom_range', 'Rango']
                ] as const).map(([currentMode, label]) => (
                  <button
                    key={currentMode}
                    type="button"
                    className={`admin-metrics-mode-button ${
                      savedInstallmentsFilters.mode === currentMode ? 'active' : ''
                    }`}
                    onClick={() => updateSavedInstallmentsFilterMode(currentMode)}
                  >
                    {label}
                  </button>
                ))}

                {loadingSavedInstallments ? (
                  <div className="admin-metrics-inline-loader">
                    <CircularProgress size={16} thickness={5} />
                    <span>Recalculando...</span>
                  </div>
                ) : null}
              </div>

              <div className="admin-metrics-saved-filters">
                {savedInstallmentsFilters.mode === 'year' ? (
                  <TextField
                    label="Año"
                    type="number"
                    value={savedInstallmentsFilters.year}
                    onChange={event => updateSavedInstallmentsField('year', event.target.value)}
                    variant="outlined"
                    size="small"
                    inputProps={{ min: 2000, max: 2100 }}
                  />
                ) : null}

                {savedInstallmentsFilters.mode === 'month' ? (
                  <TextField
                    label="Mes"
                    type="month"
                    value={savedInstallmentsFilters.month}
                    onChange={event => updateSavedInstallmentsField('month', event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                  />
                ) : null}

                {savedInstallmentsFilters.mode === 'custom_range' ? (
                  <>
                    <TextField
                      label="Fecha inicio"
                      type="date"
                      value={savedInstallmentsFilters.startDate}
                      onChange={event => updateSavedInstallmentsField('startDate', event.target.value)}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      size="small"
                    />
                    <TextField
                      label="Fecha fin"
                      type="date"
                      value={savedInstallmentsFilters.endDate}
                      onChange={event => updateSavedInstallmentsField('endDate', event.target.value)}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      size="small"
                    />
                  </>
                ) : null}

                <TextField
                  label="Banco"
                  select
                  value={savedInstallmentsFilters.bank}
                  onChange={event => updateSavedInstallmentsField('bank', event.target.value)}
                  variant="outlined"
                  size="small"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {bankList.map(bankName => (
                    <MenuItem value={bankName} key={bankName}>
                      {bankName}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </div>
          )}

          {activeError ? <div className="admin-metrics-error">{activeError}</div> : null}

          {isHoursModule ? (
            <>
              <div className="admin-metrics-summary-grid">
                <div className="admin-metrics-summary-card">
                  <Typography variant="caption">Horas registradas</Typography>
                  <div className="admin-metrics-summary-value">{formatSeconds(totalSeconds)}</div>
                </div>
                <div className="admin-metrics-summary-card">
                  <Typography variant="caption">Horas promedio por empleado por {averagePeriodLabel}</Typography>
                  <div className="admin-metrics-summary-value">{formatSeconds(averageSecondsPerEmployee)}</div>
                </div>
                <div className="admin-metrics-summary-card">
                  <Typography variant="caption">Empleados con registros</Typography>
                  <div className="admin-metrics-summary-value">{summaries.length}</div>
                </div>
                <div className="admin-metrics-summary-card">
                  <Typography variant="caption">Bloques cerrados</Typography>
                  <div className="admin-metrics-summary-value">{visibleEntriesCount}</div>
                </div>
              </div>

              {bootstrapping || (loadingEntries && !bootstrapReady) ? (
                <Box display="flex" justifyContent="center" padding="40px 0">
                  <CircularProgress />
                </Box>
              ) : null}

              {!bootstrapping && !loadingEntries && visibleEntriesCount === 0 && !error ? (
                <div className="admin-metrics-empty">
                  No hay horas registradas para el periodo actual. Cambia el filtro o busca un empleado distinto.
                </div>
              ) : null}

              <div
                className={`admin-metrics-content-grid ${
                  selectedEmployee
                    ? 'admin-metrics-content-grid--detail-open'
                    : 'admin-metrics-content-grid--table-only'
                }`}
              >
                <div className={`admin-metrics-table-wrap ${loadingEntries ? 'is-loading' : ''}`}>
                  <div className="admin-metrics-table-header">
                    <div>
                      <Typography variant="h6">Resumen por empleado</Typography>
                      <Typography variant="body2" className="admin-metrics-subtle">
                        Haz clic sobre un empleado para ver el detalle de sus horas en el periodo activo.
                      </Typography>
                    </div>
                    <div className="admin-metrics-table-chip">
                      {summaries.length} {summaries.length === 1 ? 'empleado' : 'empleados'}
                    </div>
                  </div>
                  <div className="admin-metrics-table-scroll">
                    <table className="admin-metrics-table">
                      <thead>
                        <tr>
                          <th>Empleado</th>
                          <th>Rol</th>
                          <th>Ubicacion</th>
                          <th>Horas</th>
                          <th>Dias</th>
                          <th>Bloques</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!summaries.length ? (
                          <tr>
                            <td colSpan={6}>
                              <div className="admin-metrics-table-empty">
                                No hay empleados con registros en este filtro.
                              </div>
                            </td>
                          </tr>
                        ) : null}
                        {summaries.map(summary => {
                          const employeeToSelect = summary.employee || {
                            _id: summary.userId,
                            name: summary.userId,
                            role: 'Empleado'
                          }

                          return (
                            <tr
                              key={summary.userId}
                              className={selectedEmployee?._id === summary.userId ? 'is-selected' : ''}
                              onClick={() => selectEmployee(employeeToSelect)}
                            >
                              <td>
                                <div className="admin-metrics-employee-cell">
                                  <span className="admin-metrics-employee-name">
                                    {summary.employee?.name || summary.userId}
                                  </span>
                                  <span className="admin-metrics-employee-hint">Ver detalle</span>
                                </div>
                              </td>
                              <td>{formatRoleLabel(summary.employee?.role)}</td>
                              <td>{formatLocationTypeLabel(summary.lastLocationType)}</td>
                              <td>{formatSeconds(summary.totalSeconds)}</td>
                              <td>{summary.daysCount}</td>
                              <td>{summary.entriesCount}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {loadingEntries ? (
                    <div className="admin-metrics-table-loader">
                      <CircularProgress size={24} thickness={5} />
                      <span>Cargando registros...</span>
                    </div>
                  ) : null}
                </div>

                {selectedEmployee ? (
                  <div className="admin-metrics-detail-card">
                    <Box marginBottom="16px">
                      <Typography variant="h6">Detalle del empleado</Typography>
                    </Box>
                    <Box marginTop="12px" marginBottom="18px">
                      <Typography variant="subtitle1">{selectedEmployee.name}</Typography>
                      <Typography variant="body2" className="admin-metrics-subtle">
                        {formatRoleLabel(selectedEmployee.role)}
                      </Typography>
                    </Box>

                    {!selectedEmployeeEntries.length ? (
                      <div className="admin-metrics-empty" style={{ marginTop: 16, marginBottom: 0 }}>
                        Este empleado no tiene registros en el periodo activo.
                      </div>
                    ) : (
                      <div className="admin-metrics-detail-list">
                        {selectedEmployeeEntries.map(entry => (
                          <div key={entry.entryId} className="admin-metrics-detail-item">
                            <Typography variant="subtitle2">{formatDateLabel(entry.workDate)}</Typography>
                            <div className="admin-metrics-detail-meta">
                              <span>Inicio: {formatTimestamp(entry.startedAt)}</span>
                              <span>Fin: {formatTimestamp(entry.endedAt)}</span>
                              <span>Horas: {formatSeconds(Number(entry.workedSeconds || 0))}</span>
                            </div>
                            <div className="admin-metrics-detail-location">
                              {formatLocationDetail(entry.locationType, entry.address, entry.nearestOfficeName)}
                            </div>
                            <div className="admin-metrics-detail-pauses">
                              <span className="admin-metrics-detail-pauses-label">Pausas tomadas:</span>
                              {loadingSelectedEmployeePauses ? (
                                <span className="admin-metrics-detail-pause-pill is-muted">Cargando...</span>
                              ) : selectedEmployeePausesByEntryId[entry.entryId]?.length ? (
                                selectedEmployeePausesByEntryId[entry.entryId].map(pause => (
                                  <span key={pause.pauseId} className="admin-metrics-detail-pause-pill">
                                    {getPauseLabel(pause.type)} · {formatSeconds(Number(pause.durationSeconds || 0))}
                                  </span>
                                ))
                              ) : (
                                <span className="admin-metrics-detail-pause-pill is-muted">Sin pausas</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <div className="admin-metrics-kpi-grid">
              <div className="admin-metrics-summary-card">
                <Typography variant="caption">Cuotas ahorradas totales</Typography>
                <div className="admin-metrics-summary-value">
                  {loadingSavedInstallments ? '...' : formatNumber(savedInstallmentsSummary.savedInstallments)}
                </div>
              </div>
              <div className="admin-metrics-summary-card">
                <Typography variant="caption">Intereses ahorrados totales</Typography>
                <div className="admin-metrics-summary-value">
                  {loadingSavedInstallments ? '...' : formatCurrency(savedInstallmentsSummary.savedTaxes)}
                </div>
              </div>
              <div className="admin-metrics-summary-card">
                <Typography variant="caption">Casos totales</Typography>
                <div className="admin-metrics-summary-value">
                  {loadingSavedInstallments ? '...' : formatNumber(savedInstallmentsSummary.totalCases)}
                </div>
              </div>
              <div className="admin-metrics-summary-card">
                <Typography variant="caption">Casos exitosos</Typography>
                <div className="admin-metrics-summary-value">
                  {loadingSavedInstallments ? '...' : formatNumber(savedInstallmentsSummary.successCases)}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

export default AdminMetricsView
